import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import Header from "@/components/Header";
import LegalFooter from "@/components/LegalFooter";
import LiveStats from "@/components/LiveStats";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Lock, TrendingUp } from "lucide-react";

const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1704040686428-7534b262d0d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg2MzkyMjIxfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { lang, user } = useApp();
  const navigate = useNavigate();

  const goCta = () => navigate(user ? "/dashboard" : "/login");
  const scrollHow = () => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#F5F0E6] text-[#1A1610] flex flex-col overflow-x-hidden">
      <Header showNav />

      {/* HERO — minimalist, no images (per reference) */}
      <section className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 pt-16 pb-24 sm:pt-24 sm:pb-32 w-full">
        <p
          className="text-xs sm:text-sm tracking-[0.4em] uppercase text-[#B8965A] mb-8"
          data-testid="landing-eyebrow"
        >
          {t("landing_eyebrow", lang)}
        </p>

        <h1
          className="text-4xl sm:text-6xl lg:text-7xl font-light leading-[1.1] max-w-5xl"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
          data-testid="landing-title"
        >
          <span className="text-[#1A1610] block">{t("landing_line1", lang)}</span>
          <span className="text-[#B8965A] block">{t("landing_line2", lang)}</span>
        </h1>

        <div className="mt-10 h-px w-24 bg-[#B8965A]" aria-hidden="true" />

        <div className="mt-10 max-w-2xl space-y-1 text-[#4A3F30] text-base sm:text-lg">
          <p>{t("landing_desc1", lang)}</p>
          <p>{t("landing_desc2", lang)}</p>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <button
            onClick={goCta}
            data-testid="landing-cta-join"
            className="bg-[#B8965A] text-white hover:bg-[#a08248] tracking-[0.25em] uppercase text-xs sm:text-sm px-10 h-14 transition-colors duration-200"
          >
            {t("cta_join", lang)}
          </button>
          <button
            onClick={scrollHow}
            data-testid="landing-cta-explore"
            className="border border-[#B8965A] text-[#B8965A] hover:bg-[#B8965A] hover:text-white tracking-[0.25em] uppercase text-xs sm:text-sm px-10 h-14 transition-colors duration-200"
          >
            {t("cta_explore", lang)}
          </button>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 sm:py-32 border-t border-[#E6DFCF]">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl mb-16">
            <p className="text-xs tracking-[0.4em] uppercase text-[#B8965A] mb-4">01 — 03</p>
            <h2 className="text-4xl sm:text-5xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {t("how_it_works", lang)}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D8CFBB]">
            {[
              { icon: Shield, title: t("step1_title", lang), desc: t("step1_desc", lang), num: "01" },
              { icon: Lock, title: t("step2_title", lang), desc: t("step2_desc", lang), num: "02" },
              { icon: TrendingUp, title: t("step3_title", lang), desc: t("step3_desc", lang), num: "03" },
            ].map((s) => (
              <div key={s.num} className="bg-[#FFFAF0] p-8 lg:p-12 group">
                <div className="flex items-center justify-between mb-8">
                  <span className="text-[#B8965A] text-xs tracking-[0.3em]">{s.num}</span>
                  <s.icon className="h-5 w-5 text-[#B8965A] transition-transform duration-300 group-hover:-translate-y-1" />
                </div>
                <h3 className="text-2xl mb-4 font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{s.title}</h3>
                <p className="text-sm text-[#6B5F4C] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Membership roles — chi entra nel club */}
          <div className="mt-20">
            <div className="max-w-3xl mb-10">
              <p className="text-xs tracking-[0.4em] uppercase text-[#B8965A] mb-3">
                {t("club_who", lang)}
              </p>
              <h2 className="text-3xl sm:text-4xl font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                {t("club_headline", lang)}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="membership-types">
              {[
                { key: "agent", title: t("role_agent", lang), desc: t("role_agent_desc", lang) },
                { key: "owner", title: t("role_owner", lang), desc: t("role_owner_desc", lang) },
                { key: "buyer", title: t("role_buyer", lang), desc: t("role_buyer_desc", lang) },
              ].map((r, i) => (
                <div key={r.key} className="border border-[#D8CFBB] rounded-lg p-7 bg-[#FFFAF0] hover:border-[#B8965A] hover:-translate-y-1 transition-transform duration-300" data-testid={`membership-${r.key}`}>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-4">Membership · {String(i+1).padStart(2,'0')}</p>
                  <h4 className="text-xl font-light mb-3 text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>{r.title}</h4>
                  <p className="text-sm text-[#6B5F4C] leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Escrow trust band */}
          <div className="mt-16 border border-[#B8965A]/30 bg-gradient-to-r from-[#B8965A]/5 via-transparent to-[#B8965A]/5 p-8 lg:p-12 rounded-lg" data-testid="escrow-band">
            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
              <div className="h-14 w-14 rounded-full border border-[#B8965A] flex items-center justify-center">
                <Shield className="h-6 w-6 text-[#B8965A]" />
              </div>
              <div>
                <p className="text-xs tracking-[0.4em] uppercase text-[#B8965A] mb-2">
                  {t("funds_held", lang)}
                </p>
                <h3 className="text-3xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  {t("escrow_headline", lang)}
                </h3>
                <p className="text-sm text-[#4A3F30] leading-relaxed max-w-3xl">
                  {t("escrow_notice", lang)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE TRUST NUMBERS */}
      <LiveStats />

      {/* SPLIT PORTFOLIO TEASER */}
      <section className="border-t border-[#D8CFBB]">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[560px]">
            <img src={INTERIOR_IMG} alt="Interior" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="p-12 lg:p-20 flex flex-col justify-center bg-[#EEE5D0]">
            <p className="text-xs tracking-[0.4em] uppercase text-[#B8965A] mb-4">Portfolio</p>
            <h2 className="text-4xl sm:text-5xl font-light mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {t("portfolio_headline", lang)}
            </h2>
            <p className="text-[#6B5F4C] mb-8 leading-relaxed">{t("hero_sub", lang)}</p>
            <div>
              <Button
                onClick={goCta}
                data-testid="cta-portfolio-button"
                className="bg-transparent border border-[#B8965A] text-[#B8965A] hover:bg-[#B8965A] hover:text-white rounded-full px-8 h-12 transition-colors duration-200"
              >
                {t("cta_signin", lang)}
                <ArrowRight className="ms-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <LegalFooter />
      </footer>
    </div>
  );
}
